import { ChatShort } from '../common/ai-chat/aiChatTypes';
import { useChatSections } from './useChatSections';
import ChatsSectionLayout from './ChatsSectionLayout';
import AgentChatListItem from './AgentChatListItem';
import CollapsibleSectionLayout from './CollapsibleSectionLayout';
import { Stack } from '@mui/material';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useRef, useMemo } from 'react';

const MIN_VIRTUALIZED_CHAT_ITEMS = 30;

const AgentChatsSections = ({
  activeChatId,
  onSelectActiveChat,
  chats,
}: {
  activeChatId?: string;
  onSelectActiveChat: (id: string) => Promise<void>;
  chats: ChatShort[];
}) => {
  const sections = useChatSections(chats);
  const virtuosoHandle = useRef<VirtuosoHandle>(null);

  // Flatten all chat items for virtualization
  const allChatItems = useMemo(() => {
    const items: Array<
      | {
          type: 'section-header';
          sectionTitle: string;
          isCollapsible: boolean;
        }
      | {
          type: 'chat';
          chat: ChatShort;
          sectionTitle: string;
        }
    > = [];

    sections.forEach((section) => {
      if (section.items.length === 0) return;

      items.push({
        type: 'section-header',
        sectionTitle: section.title,
        isCollapsible: section.isCollapsible || false,
      });

      section.items.forEach((chat) => {
        items.push({
          type: 'chat',
          chat,
          sectionTitle: section.title,
        });
      });
    });

    return items;
  }, [sections]);

  const totalChatCount = useMemo(() => {
    return sections.reduce((total, section) => total + section.items.length, 0);
  }, [sections]);

  const shouldVirtualize = totalChatCount > MIN_VIRTUALIZED_CHAT_ITEMS;

  const renderVirtualizedItem = (index: number, item: (typeof allChatItems)[0]) => {
    if (item.type === 'section-header') {
      const SectionComponent = item.isCollapsible ? CollapsibleSectionLayout : ChatsSectionLayout;
      return (
        <SectionComponent key={`${item.sectionTitle}-header`} title={item.sectionTitle}>
          <div />
        </SectionComponent>
      );
    }

    return (
      <div key={item.chat.id} >
        <AgentChatListItem
          chat={item.chat}
          isActive={activeChatId === item.chat.id}
          onClick={() => onSelectActiveChat(item.chat.id)}
          disableActions={false}
        />
      </div>
    );
  };

  const renderNonVirtualizedSections = () => (
    <Stack gap={2}>
      {sections.map((section) => {
        const empty = section.items.length === 0;
        if (empty) return null;
        const { isCollapsible } = section;
        const SectionComponent = isCollapsible ? CollapsibleSectionLayout : ChatsSectionLayout;
        return (
          <SectionComponent key={section.title} title={section.title}>
            <Stack>
              {section.items.map((chat) => (
                <AgentChatListItem
                  key={chat.id}
                  chat={chat}
                  isActive={activeChatId === chat.id}
                  onClick={() => onSelectActiveChat(chat.id)}
                  disableActions={false}
                />
              ))}
            </Stack>
          </SectionComponent>
        );
      })}
    </Stack>
  );

  if (!shouldVirtualize) {
    return <div style={{ flex: 1, minHeight: 0 }}>{renderNonVirtualizedSections()}</div>;
  }

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Virtuoso ref={virtuosoHandle} data={allChatItems} itemContent={renderVirtualizedItem} />
    </div>
  );
};

export default AgentChatsSections;
